import { useCallback, useEffect, useMemo, useState } from 'react'

import { useOrganizationId } from '~publish/contexts/AccountContext'
import { graphql } from '~publish/graphql'
import type { FragmentOf } from '~publish/graphql'

import { useCommentsCounts } from '../../../../hooks/comments/useCommentsCounts'

export const REPLIED_COMMENTS_MAX_LIMIT = 1_000

export const UserRepliesCount_Channel = graphql(`
  fragment UserRepliesCount_Channel on Channel @_unmask {
    id
  }
`)

type UseUserRepliesCountProps = {
  channels: FragmentOf<typeof UserRepliesCount_Channel>[]
  postId?: string
  days?: number
}

type UseUserRepliesCountReturn = {
  repliesCount: number
  loading: boolean
  error: boolean
  effectiveLimit: number | null
}

/**
 * Fetches the number of comments the current user has replied to within a
 * given time window. The count is scoped to the provided channels and,
 * optionally, to a single post. When multiple channels are supplied the
 * per-channel counts are summed so the caller gets a single total.
 *
 * @param channels - The channels to count replies for. An empty array skips the fetch and returns 0.
 * @param postId - Optional post ID to narrow the count to a single post.
 * @param days - How far back to look, in days. Defaults to 7.
 */
export const useUserRepliesCount = ({
  channels,
  postId,
  days = 7,
}: UseUserRepliesCountProps): UseUserRepliesCountReturn => {
  const [repliesCount, setRepliesCount] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [effectiveLimit, setEffectiveLimit] = useState<number | null>(null)

  const organizationId = useOrganizationId()
  const { fetchCounts } = useCommentsCounts()

  const channelIds = useMemo(() => channels.map((c) => c.id), [channels])

  const dateRange = useMemo(() => {
    const end = new Date()
    const start = new Date()
    start.setDate(start.getDate() - days)

    return {
      start: start.toISOString(),
      end: end.toISOString(),
    }
  }, [days])

  const fetchRepliesCount = useCallback(async (): Promise<void> => {
    if (!organizationId || channelIds.length === 0) {
      setRepliesCount(0)
      return
    }

    setLoading(true)
    setError(false)
    setEffectiveLimit(null)

    try {
      const result = await fetchCounts({
        organizationId,
        filters: {
          channelIds,
          ...(postId ? { postId: [postId] } : {}),
          isOwnReply: true,
          publishedAt: {
            start: dateRange.start,
            end: dateRange.end,
          },
        },
        limit: REPLIED_COMMENTS_MAX_LIMIT,
      })

      const counts = result.data?.commentsCounts?.counts ?? []
      const totalCount = counts.reduce((sum, c) => sum + (c.count || 0), 0)
      const maxLimit = counts.reduce<number | null>(
        (max, c) => (c.limit != null && c.limit > (max ?? 0) ? c.limit : max),
        null,
      )

      setRepliesCount(totalCount)
      setEffectiveLimit(maxLimit)
    } catch {
      setError(true)
      setRepliesCount(0)
    } finally {
      setLoading(false)
    }
  }, [
    fetchCounts,
    organizationId,
    channelIds,
    postId,
    dateRange.start,
    dateRange.end,
  ])

  useEffect(() => {
    void fetchRepliesCount()
  }, [fetchRepliesCount])

  return {
    repliesCount,
    loading,
    error,
    effectiveLimit,
  }
}
